// Файл CsvViewer.js

/**
 * Модуль «Csv Viewer» содержит логику, относящуюся к разделу «Document Attachments».
 * Он объединяет связанные обработчики, данные или элементы интерфейса и предоставляет их соседним модулям приложения.
 * Такое разделение упрощает сопровождение, тестирование и повторное использование этой части кода.
 */

import { useEffect, useState } from 'react';


const ROWS_STEP = 200;

const columnLetter = (index) => {
    let n = index + 1;
    let label = '';

    while (n > 0) {
        const rem = (n - 1) % 26;
        label = String.fromCharCode(65 + rem) + label;
        n = Math.floor((n - 1) / 26);
    }

    return label;
};

const detect_delimiter = (text) => {
    const first_line = String(text || '').split(/\r?\n/)[0] || '';
    const delimiters = [';', ',', '\t'];

    return delimiters.reduce((best, current) => {
        const current_count = first_line.split(current).length;
        const best_count = first_line.split(best).length;

        return current_count > best_count ? current : best;
    }, ';');
};

const parse_csv_line = (line, delimiter) => {
    const cells = [];
    let value = '';
    let in_quotes = false;

    for (let index = 0; index < line.length; index += 1) {
        const char = line[index];
        const next_char = line[index + 1];

        if (char === '"' && in_quotes && next_char === '"') {
            value += '"';
            index += 1;
            continue;
        }

        if (char === '"') {
            in_quotes = !in_quotes;
            continue;
        }

        if (char === delimiter && !in_quotes) {
            cells.push(value);
            value = '';
            continue;
        }

        value += char;
    }

    cells.push(value);

    return cells;
};

const parse_csv = (text) => {
    const clean_text = String(text || '').replace(/^\uFEFF/, '');
    const delimiter = detect_delimiter(clean_text);

    return clean_text
        .split(/\r?\n/)
        .filter((line) => line.trim() !== '')
        .map((line) => parse_csv_line(line, delimiter));
};


const CsvViewer = ({ blob }) => {
    const [rows, setRows] = useState(null);
    const [visibleRows, setVisibleRows] = useState(ROWS_STEP);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!blob)
            return;

        let cancelled = false;

        setError('');
        blob.text()
            .then((result) => {
                if (cancelled)
                    return;

                setRows(parse_csv(result));
                setVisibleRows(ROWS_STEP);
            })
            .catch(() => {
                if (!cancelled)
                    setError('Не удалось отобразить csv');
            });

        return () => {
            cancelled = true;
        };
    }, [blob]);

    if (error) {
        return (
            <div className="file-viewer__message flex-column flex-align-center file-viewer__message--error">
                {error}
            </div>
        );
    }

    if (!rows)
        return null;

    if (rows.length === 0) {
        return (
            <div className="file-viewer__message flex-column flex-align-center">
                Пустой файл
            </div>
        );
    }

    const shownRows = rows.slice(0, visibleRows);
    const colCount = rows.reduce(
        (max, row) => Math.max(max, row.length), 0);

    return (
        <div className="excel-viewer flex-column csv-viewer">
            <div className="excel-viewer__table-wrap">
                <table className="excel-viewer__table">
                    <thead>
                        <tr>
                            <th className="excel-viewer__corner" />
                            {Array.from(
                                { length: colCount },
                                (_item, ci) => (
                                    <th
                                        key={ci}
                                        className="excel-viewer__colhead"
                                    >
                                        {columnLetter(ci)}
                                    </th>
                                )
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {shownRows.map((row, ri) => (
                            <tr key={ri}>
                                <th className="excel-viewer__rowhead">
                                    {ri + 1}
                                </th>
                                {Array.from(
                                    { length: colCount },
                                    (_item, ci) => (
                                        <td key={ci}>
                                            {row[ci] !== undefined
                                                && row[ci] !== null
                                                ? String(row[ci])
                                                : ''}
                                        </td>
                                    )
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {rows.length > visibleRows && (
                <button
                    type="button"
                    className="file-viewer__download-btn excel-viewer__more"
                    onClick={() =>
                        setVisibleRows((value) => value + ROWS_STEP)}
                >
                    Показать ещё ({rows.length - visibleRows})
                </button>
            )}
        </div>
    );
};


export default CsvViewer;
